import { ReactNode, useMemo } from "react";
import { Link } from "react-router-dom";
import {
	describeResult,
	HelpSearchEntry,
	HelpSearchKind,
	TextRange,
} from "../../utils/helpCentre/search";

/** More than this and the reader is asked to add words. */
const MAX_SHOWN = 50;

const KIND_LABELS: Record<HelpSearchKind, string> = {
	topic: "Topic",
	section: "Guide",
	tab: "Guide",
	faq: "FAQ",
	video: "Video",
	defaultFaq: "FAQ",
};

/** `text` with its matching words in `<mark>`: React elements, never HTML. */
function Highlighted({
	text,
	ranges,
}: Readonly<{ text: string; ranges: TextRange[] }>) {
	const parts: ReactNode[] = [];
	let from = 0;
	// Ranges are merged, so each start is unique.
	ranges.forEach(([start, end]) => {
		if (start > from) parts.push(text.slice(from, start));
		parts.push(<mark key={start}>{text.slice(start, end)}</mark>);
		from = end;
	});
	if (from < text.length) parts.push(text.slice(from));
	return <>{parts}</>;
}

interface SearchResultsProps {
	/** The matches, best first, as `searchHelp` returns them. */
	results: HelpSearchEntry[];
	terms: string[];
	query: string;
}

/** Search results as links to where each match is, with a snippet of it. */
function SearchResults({
	results,
	terms,
	query,
}: Readonly<SearchResultsProps>) {
	const shown = useMemo(
		() =>
			results.slice(0, MAX_SHOWN).map((entry) => ({
				entry,
				...describeResult(entry, terms),
			})),
		[results, terms]
	);

	if (!results.length) {
		return (
			<p className="help-empty">
				No results for “{query.trim()}”. Try other words, or contact support
				below.
			</p>
		);
	}

	return (
		<>
			<ul className="help-results">
				{shown.map(({ entry, titleRanges, snippet, snippetRanges }) => (
					<li key={entry.order}>
						<Link className="help-result" to={entry.to}>
							<span className="help-result__head">
								<span className="help-result__kind">
									{KIND_LABELS[entry.kind]}
								</span>
								<span className="help-result__title">
									<Highlighted text={entry.title} ranges={titleRanges} />
								</span>
							</span>
							<span className="help-result__context">{entry.context}</span>
							{snippet && (
								<span className="help-result__snippet">
									<Highlighted text={snippet} ranges={snippetRanges} />
								</span>
							)}
						</Link>
					</li>
				))}
			</ul>
			{results.length > shown.length && (
				<p className="help-results__more">
					Showing {shown.length} of {results.length} results. Add words to
					narrow the search.
				</p>
			)}
		</>
	);
}

export default SearchResults;
